/** Auxiliary structure of a single assistant message — everything beyond the
 *  text body. Specifically: the expandable thinking block scaffold,
 *  sub-ask blocks, and the message's correlation id.
 *
 *  NOT state — this is computed by `buildAssistantMessageMeta(parts)` from a
 *  UI message's `parts` array at render time. */

import type { Action } from '../types/actions';
import type { Source, SourcesSummary } from '../types/sources';
import type { FeedbackRating } from '../types/transport';

export type ToolCallStatus = 'running' | 'ok' | 'error';

/** One A2UI envelope as streamed via `data-a2ui` parts. Structural; the inner
 *  shape is validated by `@a2ui/web_core` at render time, not in the meta layer. */
export type A2UIEnvelope = Record<string, unknown> & { version: string };

/** Raw LLM reasoning text buffered just before this row. Each row owns the
 *  reasoning that arrived in the stream right before it was created — the
 *  "thinking-before-tool-call" pattern. */
interface RowReasoning {
  reasoning?: string;
}

export type ReasoningRow =
  | ({
      kind: 'reasoning';
      stepIndex: number;
      text: string;
      skillName?: string | null;
      /** Stable per-tool key (server `data-thinking.groupKey`) used to collapse
       *  repeated calls to the same tool into one row, even when other steps are
       *  interleaved between them. Absent for non-tool reasoning steps (e.g.
       *  skill thinking), which must never group. */
      groupKey?: string;
      /** The line to show when this row is collapsed with others sharing its
       *  groupKey (server `data-thinking.groupLabel`): the action without what
       *  each call was about, e.g. "Read internal guidance". */
      groupLabel?: string;
    } & RowReasoning)
  | ({
      kind: 'tool';
      stepIndex: number;
      toolCallId: string;
      toolName: string;
      displayName: string;
      skillName?: string | null;
      status: ToolCallStatus;
    } & RowReasoning)
  | ({
      kind: 'skill';
      stepIndex: number;
      skillName: string;
    } & RowReasoning);

export interface ThinkingState {
  title: string;
  /** Human-readable title for a sub-ask (e.g. "Looking up policy 123") —
   *  renderer prefers `label` over `title` when present. */
  label?: string;
  /** Rich timeline rows. The canonical representation of the thinking
   *  block — flat `steps` (V1 legacy) is gone. */
  rows: ReasoningRow[];
  /** Kebab-case skill id when a skill was invoked. */
  skillId?: string | null;
  /** Buffer for reasoning deltas that have arrived but not yet been attached
   *  to a row. Drains into the next row that gets created. */
  pendingReasoning?: string;
  /** True once the model has emitted its `data-reasoning` final marker. */
  reasoningDone?: boolean;
  /** True once the message has been marked complete (via `data-done`). */
  isComplete: boolean;
  /** Correlation id for compound-ask sub-asks. Single-ask thinking does
   *  not set this. */
  subAskId?: string;
}

export interface AssistantMessageMeta {
  /** Default thinking block (single-ask traffic). null when no reasoning
   *  has been emitted for this message yet. */
  thinking: ThinkingState | null;
  /** Per-sub-ask thinking states for compound asks. Insertion order mirrors
   *  the order in which sub-asks were announced via `data-sub-ask-start`. */
  subAskThinkings: ThinkingState[];
  /** Per-message correlationId from `data-done`. null until the message
   *  has been finalized. */
  correlationId: string | null;
  /** Server-assigned messageId from `data-done`. Differs from the AI SDK
   *  `UIMessage.id` for live messages — AI SDK generates ids client-side,
   *  but the feedback API is keyed by the server's persisted id. Historical
   *  messages set `UIMessage.id` to the server id directly, so a `null`
   *  here means "fall back to `UIMessage.id`". */
  serverMessageId: string | null;
  /** Persisted feedback rating from a prior session (hydrated via
   *  `getConversation` history). null when not yet rated. Live messages
   *  start as null; the user's first vote updates UI optimistically. */
  feedbackRating?: FeedbackRating | null;
  /** Action cards emitted by the server via `data-actions`. Accumulated —
   *  the backend may emit the part more than once per stream. Rendered
   *  beneath the message text by the host-registered `ActionCardRenderer`s
   *  in `MooChatConfig.actionCards`. */
  actions: Action[];
  /** Citation sources emitted by `data-sources`. Indexed 1-based by `[N]`
   *  tokens in the assistant text (`[1]` → `sources[0]`). */
  sources: Source[];
  /** Opaque counts summary from `data-sources-summary`. Shown beneath the
   *  message when the assistant consulted sources but didn't inline
   *  `[N]` citations. */
  sourcesSummary: SourcesSummary | null;
  /** Ordered list of A2UI envelopes streamed via `data-a2ui` parts. Replayed
   *  through a fresh MessageProcessor on hydrate; appended incrementally live. */
  a2uiMessages: A2UIEnvelope[];
}

export const emptyAssistantMessageMeta = (): AssistantMessageMeta => ({
  thinking: null,
  subAskThinkings: [],
  correlationId: null,
  serverMessageId: null,
  feedbackRating: null,
  actions: [],
  sources: [],
  sourcesSummary: null,
  a2uiMessages: [],
});
